import React from 'react'

import Autosuggest from 'react-autosuggest'
import { withTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import { ErrorContainer } from 'components/common'
import Icon, { IconNames } from 'components/icons'
import mediaQueries from 'components/media-queries'
import Message from 'components/message'
import theme from 'components/theme'
import { isFetchingSuggestions } from 'selectors/suggestions'

import { Container, InputContainer, Suggestion, ErrorMessageContainer } from './styled'

export const IconWithMargin = styled(Icon)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  margin: 8px;
  ${({ light }) =>
    light &&
    `
    margin: 10px;
    width: 24px;
    height: 24px;
  `}
  ${mediaQueries.tablet`
    ${({ light }) =>
      light &&
      `
      margin: 12px 6px;
      width: 19px;
      height: 19px;
    `}
  `}
`

class Autocomplete extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      inputFocus: false,
      selected: this.props.value && this.props.value.display,
      value: this.props.value ? this.props.value.display : '',
      suggestions: []
    }

    this.onChange = this.onChange.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.setInputFocus = this.setInputFocus.bind(this)
    this.getSuggestionValue = this.getSuggestionValue.bind(this)
    this.renderInputComponent = this.renderInputComponent.bind(this)
    this.onSuggestionsClearRequested = this.onSuggestionsClearRequested.bind(this)
  }

  getValue(value) {
    if (value) {
      return value.display ? value.display : value
    }

    return ''
  }

  componentDidMount() {
    this.messageId = uuid()
  }

  componentDidUpdate(prevProps) {
    const { value: currentValue } = this.props
    const { value: previousValue } = prevProps

    if (currentValue && previousValue !== currentValue) {
      this.setState({
        value: this.getValue(currentValue)
      })
    }
  }

  onChange(_, { newValue }) {
    this.setState({ value: newValue, selected: false })
    this.props.onChange && this.props.onChange(newValue)
  }

  // When suggestion is clicked, Autosuggest needs to populate the input
  // based on the clicked suggestion. Teach Autosuggest how to calculate the
  // input value for every given suggestion.
  getSuggestionValue(suggestion) {
    return suggestion.display
  }

  setInputFocus(inputFocus = false) {
    const { onBlur } = this.props
    const { suggestionSelected, selected, value } = this.state

    !inputFocus &&
      !selected &&
      suggestionSelected?.display !== value &&
      this.props.onSuggestionSelected(null)

    const updateState = !selected ? { suggestionSelected: null } : {}

    setTimeout(
      () =>
        this.setState(
          {
            inputFocus,
            ...updateState
          },
          () => !inputFocus && onBlur && onBlur()
        ),
      1
    )
  }

  isPrintableCharacter = char =>
    (char > 47 && char < 58) || // number keys
    char === 32 || // spacebar
    (char > 64 && char < 91) || // letter keys
    (char > 95 && char < 112) || // numpad keys
    (char > 185 && char < 193) || // ;=,-./` (in order)
    (char > 218 && char < 223) ||
    char === 229 // android softkey always gives 229

  onKeyDown(ev) {
    if (this.props.isLoading) {
      ev.preventDefault()
    }

    if (this.state.selected && this.isPrintableCharacter(ev.keyCode)) {
      this.onChange(null, { newValue: '' })
    }
  }

  renderInputComponent(inputProps) {
    const icon = this.props.inputIcon || IconNames.Airplane
    const color = this.props.inputColor || theme.colors.blue
    const e2eClass = this.props.e2eClass
    const hiddenIcon = this.props.hiddenIcon

    return (
      <InputContainer
        error={this.props.error}
        inputFocus={this.state.inputFocus}
        flatStyle={this.props.flatStyle}
      >
        {!hiddenIcon && (
          <IconWithMargin name={icon} color={color} size={30} light={this.props.light} />
        )}
        <input
          {...inputProps}
          type="text"
          aria-autocomplete={undefined}
          style={hiddenIcon && { paddingLeft: 10 }}
          className={`${inputProps.className} suggestion-input${e2eClass ? `-${e2eClass}` : ''}`}
          tabIndex={this.props.isResearch && !this.props.isExpanded ? -1 : undefined}
        />
        {this.props.rightButton}
      </InputContainer>
    )
  }

  renderSuggestion(suggestion) {
    const display = suggestion.display || ''

    return (
      <Suggestion suggestionClassName={suggestion.id} value={display}>
        <Icon name={IconNames.Airplane} color={theme.colors.dustyGray} size={16} />
        <span dangerouslySetInnerHTML={{ __html: suggestion.highlight }} />
      </Suggestion>
    )
  }

  renderSuggestionsContainer({ containerProps, children }) {
    return (
      <div {...containerProps} aria-label="autosuggest-list">
        {children}
      </div>
    )
  }

  onSuggestionsClearRequested() {
    this.setState({ suggestions: [] })
  }

  handleSuggestionSelection = (event, suggestionData) => {
    this.setState(
      {
        suggestionSelected: suggestionData.suggestion,
        selected: true
      },
      () => {
        this.props.onSuggestionSelected(suggestionData.suggestion)
      }
    )
  }

  getNoSuggestionsContent() {
    const { suggestions = [], isFetching, name } = this.props
    const { value, selected, inputFocus } = this.state

    if (!suggestions || !value || !inputFocus) {
      return null
    }

    if (!suggestions.length && value.length >= 3 && !isFetching && !selected) {
      return (
        <ErrorMessageContainer name={name}>
          <Message type="error">
            {this.props.t('label.no-suggestions-found', { term: this.state.value })}
          </Message>
        </ErrorMessageContainer>
      )
    }

    return null
  }

  shouldRenderSuggestions = () => this.props.shouldRenderSuggestions

  render() {
    return (
      <Container
        isLoading={this.props.isLoading}
        light={this.props.light}
        name={this.props.name}
        className={this.props.className}
      >
        <Autosuggest
          id={`autosuggest${this.props.e2eClass ? '-' + this.props.e2eClass : ''}-${this.props.id}`}
          containerProps={{
            'aria-label': 'autosuggest-container',
            'aria-controls': `react-autowhatever-autosuggest-${this.props.e2eClass}`
          }}
          suggestions={this.props.suggestions}
          onSuggestionsFetchRequested={this.props.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={this.getSuggestionValue}
          renderSuggestion={this.renderSuggestion}
          renderSuggestionsContainer={this.renderSuggestionsContainer}
          renderInputComponent={this.renderInputComponent}
          onSuggestionSelected={this.handleSuggestionSelection}
          inputProps={{
            tabIndex: this.props.disableIndex ? -1 : 0,
            value: this.state.value,
            placeholder: this.props.placeholder,
            onChange: this.onChange,
            onFocus: () => this.setInputFocus(true),
            onBlur: () => this.setInputFocus(),
            onKeyDown: this.onKeyDown,
            id: this.props.id,
            'aria-invalid': !!this.props.error,
            'aria-describedby': this.props.error ? this.messageId : undefined
          }}
          error={this.props.error}
          shouldRenderSuggestions={this.shouldRenderSuggestions}
          multiSection={this.props.multiSection}
          renderSectionTitle={this.props.renderSectionTitle}
          getSectionSuggestions={this.props.getSectionSuggestions}
        />
        {this.getNoSuggestionsContent()}
        {this.props.error && (
          <ErrorContainer name={this.props.name}>
            <Message type="formInputError" id={this.messageId}>
              {this.props.error}
            </Message>
          </ErrorContainer>
        )}
      </Container>
    )
  }
}

const mapStateToProps = state => ({
  isFetching: isFetchingSuggestions(state)
})

export default withTranslation()(connect(mapStateToProps)(Autocomplete))
